import axios from 'axios';
import { tenantSlugFromHost, tenantSlugFromPathOrHost } from '../utils/tenantDomains';
import { reportClientError } from './clientErrorReporter';
import { clearStoredSessionToken, getStoredSessionToken } from './sessionTokens';
import { resolveApiBaseUrl } from './runtimeConfig';
import { getPaymentDeviceId } from './paymentDevice';
import { reportPlayerConnectionFailure } from './playerConnection';

const DEVICE_ID_STORAGE_KEY = 'teamlobby.device-id.v1';

const getDeviceId = () => {
  if (typeof window === 'undefined') return '';
  try {
    const existing = String(window.localStorage.getItem(DEVICE_ID_STORAGE_KEY) || '').trim();
    if (existing) return existing;
    const generated = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    // A restricted browser can still sign up; the server leaves the
    // device-cap check inactive when no stable identifier is available.
    return '';
  }
};

const getTenantSlug = () => {
  return tenantSlugFromPathOrHost();
};

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

export const bearerTokenFromRequestHeaders = (headers) => {
  const authorization = typeof headers?.get === 'function'
    ? headers.get('Authorization')
    : (headers?.Authorization || headers?.authorization);
  const match = String(authorization || '').trim().match(/^Bearer\s+(.+)$/i);
  return match?.[1]?.trim() || null;
};

export const authProbeFailureDisposition = ({ rejectedToken, currentToken } = {}) => {
  const rejected = String(rejectedToken || '').trim() || null;
  const current = String(currentToken || '').trim() || null;
  const isStale = Boolean(current && rejected !== current);
  return {
    isStale,
    tokenToClear: rejected,
    shouldRedirect: !isStale,
  };
};

export const authRedirectPath = (path, hostname) => {
  const slug = tenantSlugFromPathOrHost(path, hostname);
  const prefix = !tenantSlugFromHost(hostname) && slug ? `/${slug}` : '';
  const adminPath = /(^|\/)admin(\/|$)/.test(path);
  return `${prefix}${adminPath ? '/admin/auth' : '/auth'}`;
};

api.interceptors.request.use((config) => {
  const token = getStoredSessionToken();
  if (config.expectedSessionToken && config.expectedSessionToken !== token) {
    return Promise.reject(Object.assign(new Error('Your session changed. Please try again.'), { code: 'SESSION_CHANGED' }));
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenantSlug = getTenantSlug();
  if (tenantSlug) config.headers['X-Tenant-Slug'] = tenantSlug;
  const deviceId = getDeviceId();
  if (deviceId) config.headers['X-Device-Fingerprint'] = deviceId;
  if (String(config.url || '').split('?')[0].endsWith('/api/gateway/deposit')) {
    config.headers['X-Payment-Device-Id'] = getPaymentDeviceId(deviceId);
  }
  // Auto-generate idempotency key for POST requests
  if (config.method === 'post' && !config.headers['X-Idempotency-Key']) {
    config.headers['X-Idempotency-Key'] = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const failedSession = bearerTokenFromRequestHeaders(error.config?.headers);
    if (!failedSession || failedSession === getStoredSessionToken()) reportPlayerConnectionFailure(error);
    // Normalize error message: backend can return either {detail: "..."} or contract envelope {message: "...", code: "..."}
    if (error.response?.data) {
      const d = error.response.data;
      if (!d.detail && d.message) d.detail = d.message;
    }
    const requestUrl = String(error.config?.url || '');
    const baseUrl = String(error.config?.baseURL || resolveApiBaseUrl() || '');
    const apiUrl = requestUrl.startsWith('http') ? requestUrl : `${baseUrl}${requestUrl}`;
    const statusCode = error.response?.status;
    if (error.code !== 'SESSION_CHANGED' && !requestUrl.includes('/api/client-errors') && (!statusCode || statusCode >= 500)) {
      const responseData = error.response?.data;
      const detail = responseData?.detail || responseData?.message || error.message || 'API request failed';
      reportClientError({
        source: 'api',
        message: detail,
        status: statusCode,
        method: String(error.config?.method || '').toUpperCase(),
        apiUrl,
        code: responseData?.code || error.code || '',
      });
    }
    if (error.response?.status === 401) {
      const method = String(error.config?.method || '').toLowerCase();
      const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
      if (isMutation) {
        return Promise.reject(error);
      }
      const isAuthProbe = requestUrl.includes('/api/auth/me') || requestUrl.endsWith('/auth/me');
      if (!isAuthProbe) {
        return Promise.reject(error);
      }
      const rejectedToken = bearerTokenFromRequestHeaders(error.config?.headers);
      const currentToken = getStoredSessionToken({ allowLegacyFallback: true });
      const disposition = authProbeFailureDisposition({ rejectedToken, currentToken });
      // A delayed /auth/me 401 may belong to the session that existed before
      // a login/signup completed. Never let that stale response erase or
      // redirect away from the newly issued session.
      if (disposition.tokenToClear) {
        clearStoredSessionToken({ token: disposition.tokenToClear });
      }
      if (!disposition.shouldRedirect) {
        return Promise.reject(error);
      }
      const path = window.location.pathname;
      const isProtected = path.startsWith('/app') || path.startsWith('/admin') || path.match(/^\/[^/]+\/(app|admin)/);
      if (isProtected) {
        window.location.href = authRedirectPath(path, window.location.hostname);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
